'use client';

import { type CSSProperties, type JSX, useCallback, useState } from 'react';

import { Icon } from '@videoblocks/design-system';

import { CARDS } from './HelperCardCarouselCards';

function getCardStyle(offset: number, total: number): CSSProperties {
    // Add rotation for cards behind the current one - alternating between positive and negative
    const rotation = offset === 0 ? 0 : (offset % 2 === 0 ? 1 : -1) * (2 + offset * 1.5);

    return {
        opacity: offset === 0 ? 1 : 1 - offset * 0.15,
        transform: `scale(${String(1 - offset * 0.05)}) translateX(${String(offset * 16)}px) translateY(${String(offset * 8)}px) rotate(${String(rotation)}deg)`,
        zIndex: total - offset,
        pointerEvents: offset === 0 ? 'auto' : 'none',
    };
}

export function HelperCardCarousel(): JSX.Element {
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const total = CARDS.length;

    const goToPrev = useCallback((): void => {
        setActiveIndex((prev) => (prev - 1 + total) % total);
    }, [total]);

    const goToNext = useCallback((): void => {
        setActiveIndex((prev) => (prev + 1) % total);
    }, [total]);

    const goToIndex = useCallback((index: number): void => {
        setActiveIndex(index);
    }, []);

    return (
        <div className="flex flex-col h-full justify-center items-center">
            <div className="relative isolate h-[400px] w-[380px]">
                {CARDS.map((card, i) => {
                    const offset = (i - activeIndex + total) % total;
                    const style = getCardStyle(offset, total);
                    const { Component } = card;

                    return (
                        <div
                            key={card.id}
                            className="absolute left-0 top-0 origin-top-left transition-all duration-500 ease-in-out"
                            style={style}
                        >
                            <Component />
                        </div>
                    );
                })}
            </div>

            <div className="flex items-center gap-4">
                <button
                    type="button"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 transition-colors hover:bg-gray-300"
                    onClick={goToPrev}
                    aria-label="Previous card"
                >
                    <Icon name="arrowBack" width={20} height={20} />
                </button>

                <div className="flex items-center gap-2">
                    {CARDS.map((card, i) => (
                        <button
                            key={card.id}
                            type="button"
                            className={`rounded-full transition-all duration-300 ${
                                i === activeIndex ? 'h-3 w-7 bg-ocean-500' : 'h-3 w-3 bg-gray-300 hover:bg-gray-400'
                            }`}
                            onClick={() => goToIndex(i)}
                            aria-label={`Go to card ${String(i + 1)}`}
                            aria-current={i === activeIndex ? 'true' : undefined}
                        />
                    ))}
                </div>

                <button
                    type="button"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 transition-colors hover:bg-gray-300"
                    onClick={goToNext}
                    aria-label="Next card"
                >
                    <Icon name="arrowRight" width={20} height={20} />
                </button>
            </div>
        </div>
    );
}
